import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Text, TextField } from "@radix-ui/themes"
import { z } from "zod"

import InputError from "@/components/input-error"
import LinkButton from "@/components/link-button"
import ProjectLayout from "@/layouts/project-layout"
import { projectCustomersPath, projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
})

type Props = z.infer<typeof PropsSchema>

export default function NewCustomer(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project } = validatedProps

  const { data, setData, post, processing, errors } = useForm({
    email: "",
    name: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(projectCustomersPath(project.slug))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Customers", href: projectCustomersPath(project.slug) },
        { title: "New customer" },
      ]}
    >
      <Head title="New customer" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            New customer
          </Heading>
          <Text color="gray" size="2">
            Add a new customer to your project
          </Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Name
              </Text>
              <TextField.Root
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="John Doe"
              />
              <InputError message={errors.name} />
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Email address <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
                placeholder="customer@example.com"
                required
                autoFocus
              />
              <InputError message={errors.email} />
            </div>

            <Flex gap="3" justify="end" mt="4">
              <LinkButton href={projectCustomersPath(project.slug)} variant="soft" color="gray">
                Cancel
              </LinkButton>

              <Button type="submit" disabled={processing}>
                {processing ? "Creating..." : "Create customer"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
